/**
 * Share into Jarvis, web half (fd-rich-chat PH-07w). When Joseph shares a link, text, a photo or a file into the Jarvis
 * app, the app opens /chat?share_chat=<id>&share_text=<text>&share_files=<token>.<count>. This takes it once: the chat
 * becomes active, the text waits in its composer (the chat seed ChatInput already reads), each file is fetched from
 * /__jarvisapp/shared/<token>/<i> (answered by the app itself, never the network) and attached, and the share
 * parameters leave the address. Nothing is sent: Joseph reads, adds his words and taps Send.
 */
import { activeChatId, uploadAttachment } from './chat-store';

export const SHARED_PREFIX = '/__jarvisapp/shared/';
const TOKEN = /^[A-Za-z0-9]{16,64}$/;
const MAX_FILES = 10;
const MAX_TEXT = 20000;
const KEYS = ['share_chat', 'share_text', 'share_files'];

export interface SharedIn {
  chatId: number;
  text: string;
  token: string | null;
  count: number;
}

/** The share an address carries, or null when it carries none. Malformed file lists count as no files. */
export function readShare(search: string): SharedIn | null {
  const params = new URLSearchParams(search);
  if (!KEYS.some((key) => params.has(key))) return null;
  const chat = Number(params.get('share_chat'));
  const text = (params.get('share_text') ?? '').slice(0, MAX_TEXT);
  let token: string | null = null;
  let count = 0;
  const files = /^([^.]+)\.(\d{1,2})$/.exec(params.get('share_files') ?? '');
  if (files && TOKEN.test(files[1])) {
    const n = Number(files[2]);
    if (n >= 1 && n <= MAX_FILES) { token = files[1]; count = n; }
  }
  return { chatId: Number.isInteger(chat) && chat !== 0 ? chat : activeChatId.value, text, token, count };
}

const EXTENSIONS: Record<string, string> = {
  'image/jpeg': '.jpg', 'image/png': '.png', 'image/webp': '.webp', 'image/heic': '.heic', 'image/gif': '.gif',
  'application/pdf': '.pdf', 'text/plain': '.txt', 'video/mp4': '.mp4', 'audio/mpeg': '.mp3', 'audio/mp4': '.m4a',
};

function fileName(header: string | null, index: number, type: string): string {
  if (header) {
    try {
      const name = decodeURIComponent(header).replace(/[\\/\u0000-\u001f]/g, '_').trim().slice(0, 180);
      if (name) return name;
    } catch {
      // Fall through to a plain name.
    }
  }
  return `shared-${index + 1}${EXTENSIONS[type.split(';')[0].trim()] ?? ''}`;
}

async function attach(token: string, index: number): Promise<void> {
  try {
    const response = await fetch(`${SHARED_PREFIX}${token}/${index}`);
    if (!response.ok) return;
    // Only the app's own answer counts. If a request ever reaches the server instead, it answers with the chat's HTML
    // shell (200 text/html, no name); that is never a shared file (PH-07w r1 red).
    const named = response.headers.get('X-Jarvis-Name');
    const type0 = (response.headers.get('Content-Type') || '').toLowerCase();
    if (!named && type0.startsWith('text/html')) return;
    const blob = await response.blob();
    const type = response.headers.get('Content-Type') || blob.type || 'application/octet-stream';
    const file = new File([blob], fileName(response.headers.get('X-Jarvis-Name'), index, type), { type: type.split(';')[0] });
    await uploadAttachment(file);
  } catch {
    // A file the app could not hand over is skipped; the rest still arrive.
  }
}

/** Take the share in this address once. True when there was one. Never sends. */
export async function takeShare(location: Location = window.location): Promise<boolean> {
  const share = readShare(location.search);
  if (!share) return false;
  const params = new URLSearchParams(location.search);
  for (const key of KEYS) params.delete(key);
  const rest = params.toString();
  window.history.replaceState(window.history.state, '', `${location.pathname}${rest ? `?${rest}` : ''}${location.hash}`);
  activeChatId.value = share.chatId;
  if (share.text) {
    try {
      sessionStorage.setItem(`chat_seed_${share.chatId}`, share.text);
    } catch {
      // No session storage: the files still come.
    }
  }
  if (share.token) {
    for (let i = 0; i < share.count; i += 1) await attach(share.token, i);
  }
  return true;
}
